import { useCallback, useEffect, useState } from "react";
import { Cake, Award, Send, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const when = (d) => (d === 0 ? "Today" : d === 1 ? "Tomorrow" : `In ${d} days`);
const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });

// This week's birthdays & work anniversaries with a one-tap greeting email.
export const CelebrationSpotlight = () => {
    const [items, setItems] = useState(null);
    const [busy, setBusy] = useState("");

    const load = useCallback(() => {
        api.get("/dashboard/celebrations-week").then(({ data }) => setItems(data.items || [])).catch(() => setItems([]));
    }, []);
    useEffect(() => { load(); }, [load]);

    const greet = async (it) => {
        const key = `${it.kind}-${it.id}`;
        setBusy(key);
        try {
            await api.post("/dashboard/celebrations/greet", { employee_id: it.id, kind: it.kind });
            toast.success(`Greeting sent to ${it.name}`);
            setItems((xs) => xs.map((x) => (x.id === it.id && x.kind === it.kind ? { ...x, greeted: true } : x)));
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not send greeting");
        } finally {
            setBusy("");
        }
    };

    if (items === null) return null;
    return (
        <div className="bg-white border border-ink/10 p-6 sm:p-8" data-testid="celebration-spotlight">
            <div className="overline">This week</div>
            <h2 className="mt-1 font-display font-bold text-2xl text-ink">Celebrations</h2>
            <div className="mt-5 space-y-2" data-testid="celebration-list">
                {items.length === 0 && <div className="py-8 text-center text-sm text-ink/40" data-testid="celebration-empty">No birthdays or work anniversaries this week.</div>}
                {items.map((it) => {
                    const key = `${it.kind}-${it.id}`;
                    const bday = it.kind === "birthday";
                    return (
                        <div key={key} className="flex flex-wrap sm:flex-nowrap items-center gap-3 rounded-xl border border-ink/10 p-3" data-testid={`celebration-${key}`}>
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${bday ? "bg-terracotta-50 text-terracotta-500" : "bg-amber-50 text-amber-600"}`}>
                                {bday ? <Cake className="w-5 h-5" /> : <Award className="w-5 h-5" />}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold text-ink truncate">{it.name}</div>
                                <div className="text-xs text-ink/55 truncate">
                                    {bday ? "Birthday" : `${it.years} year${it.years === 1 ? "" : "s"} at work`} · {when(it.in_days)} · {dayLabel(it.date)}
                                </div>
                            </div>
                            {it.greeted ? (
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600 whitespace-nowrap" data-testid={`celebration-sent-${key}`}>
                                    <Check className="w-4 h-4" /> Sent
                                </span>
                            ) : (
                                <button onClick={() => greet(it)} disabled={busy === key || !it.has_email}
                                    title={it.has_email ? "Send a greeting email" : "No email on file"}
                                    className="inline-flex items-center gap-1.5 bg-klein text-white px-3 py-2 text-xs whitespace-nowrap disabled:opacity-50"
                                    data-testid={`celebration-greet-${key}`}>
                                    <Send className="w-3.5 h-3.5" /> {busy === key ? "Sending…" : "Send greeting"}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default CelebrationSpotlight;
